import React, { InputHTMLAttributes, useEffect, useState } from 'react';
import { EyeIcon, EyeClosedIcon } from '@primer/octicons-react';
import SpinnerLoader from '../../assets/svg/spinnerLoader.svg';
import CurrencyInputMask from './MaskInputComponents/CurrencyInputMask';
import PhoneInputMask from './MaskInputComponents/PhoneInputMask';
import BankInputMask from './MaskInputComponents/BankInputMask';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  showBlue?: boolean;
  showGreen?: boolean;
  showRed?: boolean;
  useCurrencyMask?: boolean;
  usePhoneMask?: boolean;
  useBankMask?: boolean;
  isUpdating?: boolean;
  error?: any;
  showError?: boolean;
  hasSpinner?: boolean;
  subOrSupScriptPlaceholder?: string | JSX.Element | null;
  innerRef?: React.LegacyRef<HTMLInputElement> | undefined;
  type?: string;
  inputClass?: string;
  bgClass?: string;
}

export const Input = ({
  hasSpinner,
  showBlue,
  showGreen,
  showRed,
  useCurrencyMask,
  usePhoneMask,
  useBankMask,
  subOrSupScriptPlaceholder,
  isUpdating,
  innerRef,
  error,
  showError,
  inputClass,
  type,
  bgClass,
  ...props
}: InputProps) => {
  const [inputType, setInputType] = useState('');
  const [isPasswordField, setIsPasswordField] = useState(false);
  const [hasContent, setHasContent] = useState(!!props.value);

  useEffect(() => {
    if (type) setInputType(type);
    if (type && type === 'password') setIsPasswordField(true);
  }, [type]);
  useEffect(() => {
    setHasContent(!!props.value);
  }, [props.value]);

  const BaseInput = (
    <input
      type={inputType}
      ref={innerRef}
      {...props}
      className={`h-inputHeight rounded-inputBorderRadius  ${bgClass} shadow-none  px-4
      hover:bg-neutral-12/[0.1] flex-grow outline-none
      w-full block text-base text-neutral-12
      pt-[26px] pb-2.5
      placeholder-transparent
      whitespace-nowrap  overflow-ellipsis overflow-hidden

      ${inputClass} ${showBlue && '!border-blue-00 border'} ${
        showGreen && 'border !border-pyrllrFgGreen'
      } ${showRed && 'border !border-red-00'} ${hasSpinner && 'show-spinner'}`}
      autoComplete="off"
      multiple
    />
  );

  return (
    <div className="relative p-0 mb-6">
      <div className="relative flex flex-col flex-wrap items-stretch w-full mb-3">
        <div className="flex flex-row">
          <div className="relative flex-grow float-label-input ">
            {useCurrencyMask && (
              <CurrencyInputMask
                {...props}
                className={`h-inputHeight rounded-inputBorderRadius  ${bgClass} shadow-none  px-4
                hover:bg-neutral-12/[0.1] flex-grow outline-none
                w-full block text-base text-neutral-12
                pt-[26px] pb-2.5
                placeholder-transparent
                whitespace-nowrap  overflow-ellipsis overflow-hidden

                ${inputClass} ${showBlue && '!border-blue-00 border'} ${
                  showGreen && 'border !border-pyrllrFgGreen'
                } ${showRed && 'border !border-red-00'} ${
                  hasSpinner && 'show-spinner'
                }`}
              />
            )}
            {usePhoneMask && (
              <PhoneInputMask
                {...props}
                className={`h-inputHeight rounded-inputBorderRadius  ${bgClass} shadow-none  px-4
                hover:bg-neutral-12/[0.1] flex-grow outline-none
                w-full block text-base text-neutral-12
                pt-[26px] pb-2.5
                placeholder-transparent
                whitespace-nowrap  overflow-ellipsis overflow-hidden

                ${inputClass} ${showBlue && '!border-blue-00 border'} ${
                  showGreen && 'border !border-pyrllrFgGreen'
                } ${showRed && 'border !border-red-00'} ${
                  hasSpinner && 'show-spinner'
                }`}
                autoComplete="off"
                value={props.value?.toString()}
              />
            )}
            {useBankMask && (
              <BankInputMask
                {...props}
                className={`h-inputHeight rounded-inputBorderRadius  ${bgClass} shadow-none  px-4
                hover:bg-neutral-12/[0.1] flex-grow outline-none
                w-full block text-base text-neutral-12
                pt-[26px] pb-2.5
                placeholder-transparent
                whitespace-nowrap  overflow-ellipsis overflow-hidden

                ${inputClass} ${showBlue && '!border-blue-00 border'} ${
                  showGreen && 'border !border-pyrllrFgGreen'
                } ${showRed && 'border !border-red-00'} ${
                  hasSpinner && 'show-spinner'
                }`}
                autoComplete="off"
                value={props.value?.toString()}
              />
            )}

            {!useBankMask && !usePhoneMask && !useCurrencyMask && BaseInput}

            <label
              className={`  w-[100%] absolute top-0 left-0 p-4  text-neutral-40 pointer-events-none transition duration-300 ease-in-outbg-white text-grey-darker origin-[10%]  whitespace-nowrap text-ellipsis overflow-x-hidden
         ${hasContent ? 'has-content' : ''}`}
              htmlFor={props.id}
            >
              {props.placeholder}
              {subOrSupScriptPlaceholder !== null && subOrSupScriptPlaceholder}
            </label>
          </div>
        </div>
        {hasSpinner && (
          <span className="absolute bg-transparent border-none z-10 top-[35%] right-2 ">
            {isUpdating && (
              <i>
                <img
                  src={SpinnerLoader}
                  alt="spinner"
                  className="animate-spinner "
                />
              </i>
            )}

            {isPasswordField &&
              (inputType === 'password' ? (
                <i
                  onClick={() => setInputType('text')}
                  role="button"
                  aria-hidden="true"
                >
                  <EyeIcon size={24} />
                </i>
              ) : (
                <i
                  onClick={() => setInputType('password')}
                  role="button"
                  aria-hidden="true"
                >
                  <EyeClosedIcon size={24} />
                </i>
              ))}
          </span>
        )}
      </div>
      {!showError ? (
        <div className="absolute h-4 px-4 text-xs top-14 text-red-00 ">
          {error}
        </div>
      ) : null}
    </div>
  );
};

Input.defaultProps = {
  showBlue: false,
  showGreen: false,
  showRed: false,
  useCurrencyMask: false,
  usePhoneMask: false,
  useBankMask: false,
  isUpdating: false,
  error: '',
  showError: false,
  hasSpinner: false,
  subOrSupScriptPlaceholder: null,
  innerRef: null,
  type: '',
  bgClass: 'bg-neutral-95',
};

export default Input;
